import { client as apolloClient } from '../apollo-graphql/index'
import { ENSURE_COMMUNITY_PROFILE } from './query'
import { getLogger } from '../../log/index'

/**
 * Create the signed-in member's Community profile if they have none, at the END of signup.
 *
 * ## Why the client has to be the one to ask
 *
 * Brooklyn cannot do this on its own initiative. The account is created by the **auth** API —
 * Brooklyn's own `user.create` mints an admin JWT and delegates — and `cart-begin` is email
 * capture that runs before any account exists. So there is no "new account + billing complete"
 * moment inside Brooklyn to hang it on, and the clients are the only place that knows signup just
 * finished.
 *
 * ⚠️ The client says only WHEN. The mutation takes no arguments, so a client cannot name somebody
 * else's profile or make one public by passing the wrong value — the server decides everything
 * written: private, display name from the auth username, and deliberately NOT counted as
 * self-edited, so the member still meets "Join the Conversation" the first time they try to post.
 *
 * ## Why it matters
 *
 * A mention resolves its recipients through `user_profiles.uuid`, so an account with no row can
 * be `@`-mentioned and is **never notified** — the name renders and nothing happens. The same row
 * is what gives them a name in a circle roster.
 *
 * ## Failure is deliberately silent
 *
 * ⚠️ Never throws and never blocks. Somebody whose profile could not be provisioned has still
 * paid; turning a background nicety into a failed or delayed order confirmation would be a far
 * worse outcome than the one it prevents. If it fails they are simply unmentionable until the
 * `provisionCommunityProfiles` backfill reaches them.
 *
 * Idempotent server-side — an existing profile is returned untouched and is never demoted to
 * provisioned — so calling it on every completed order is safe.
 *
 * @param auth the viewer's JWT, which the GraphQL layer forwards as the caller identity
 */
export async function ensureCommunityProfile (auth?: string | null): Promise<void> {
  if (!auth) return
  try {
    await apolloClient.mutate({
      mutation: ENSURE_COMMUNITY_PROFILE,
      context: { jwt: auth },
    })
  } catch (error) {
    // ⚠️ Swallowed on purpose. See the note above: a failed provision must never surface to
    // somebody who has just paid.
    getLogger()?.warn?.({ error }, 'community profile provisioning on signup failed')
  }
}

export default ensureCommunityProfile
